import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authService } from '../services/authService';
import { ADMIN_ROLES, EMPLOYEE_ROLES } from '../constants';
import { normalizeUserModel } from '../shared/userModel';

const AuthContext = createContext(null);

const mergeUserWithProfile = (currentUser, profileUser) => {
  return normalizeUserModel({ ...(currentUser || {}), ...(profileUser || {}) });
};

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(() => {
    try {
      const saved = localStorage.getItem('tema_auth');
      if (!saved) return { user: null, token: null, type: null };
      const parsed = JSON.parse(saved);
      return { ...parsed, user: normalizeUserModel(parsed?.user) };
    } catch {
      return { user: null, token: null, type: null };
    }
  });

  const persistAuth = useCallback((nextAuth) => {
    const normalizedAuth = {
      ...nextAuth,
      user: nextAuth?.user ? normalizeUserModel(nextAuth.user) : null,
    };
    setAuth(normalizedAuth);
    localStorage.setItem('tema_auth', JSON.stringify(normalizedAuth));
  }, []);

  const hydrateProfile = useCallback(async (baseAuth) => {
    if (!baseAuth?.token || !baseAuth?.user) return baseAuth?.user || null;
    try {
      const profileRes = await authService.getProfile();
      const profileUser = profileRes?.data;
      if (!profileUser) return baseAuth.user;

      const mergedUser = mergeUserWithProfile(baseAuth.user, profileUser);
      persistAuth({ ...baseAuth, user: mergedUser });
      return mergedUser;
    } catch {
      return baseAuth.user;
    }
  }, [persistAuth]);

  useEffect(() => {
    if (!auth?.token || !auth?.user) return;
    hydrateProfile(auth);
  }, []);

  const login = useCallback(async ({ email, password }) => {
    const res = await authService.login({ email, password });
    const { token, type, user } = res.data;
    const authData = { user: normalizeUserModel(user), token, type };
    persistAuth(authData);
    return hydrateProfile(authData);
  }, [hydrateProfile, persistAuth]);

  const employeeLogin = useCallback(async ({ tema_api_key, tema_user_id, name, email, phone, rso }) => {
    const res = await authService.employeeLogin({ tema_api_key, tema_user_id, name, email, phone, rso });
    const { token, type, user } = res.data;
    const authData = { user: normalizeUserModel(user), token, type };
    persistAuth(authData);
    return hydrateProfile(authData);
  }, [hydrateProfile, persistAuth]);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch {
      // Abaikan error logout API agar sesi lokal tetap dibersihkan.
    }
    setAuth({ user: null, token: null, type: null });
    localStorage.removeItem('tema_auth');
  }, []);

  const isAuthenticated = !!auth.token && !!auth.user;
  const isAdmin = isAuthenticated && ADMIN_ROLES.includes(auth.user?.role);
  const isEmployee = isAuthenticated && EMPLOYEE_ROLES.includes(auth.user?.role);

  return (
    <AuthContext.Provider value={{ ...auth, isAuthenticated, isAdmin, isEmployee, login, employeeLogin, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth harus dipakai di dalam AuthProvider');
  return ctx;
};
